import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { IqName } from '@/components/IqName';
import { useOrganization, useOrganizationList } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, IconButton, Input, Tabs } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { findClients } from '@/lib/clientSearch';
import { articleStatus, isAbandonedInterview, supersededStoryIds, ARTICLE_STATUS_LABEL, ARTICLE_STATUS_HINT, ARTICLE_STATUS_VARIANT, ARTICLE_STATUS_ORDER } from '@/lib/articleStatus';
import { apiFetch } from '@/data/api';
import { addStaff, createDemoAccount, getAdminBlogTopics, updateAdminBlogTopic, setAdminBlogTopicBatchStatus, getOutstandingGaps, remindGapSetNow, closeGapSetNow, getAdminContent, getContentMatrix, getRoamConfig, getStaff, reassignAccounts, removeStaff, setAdminBlogTopicStatus, syncStaffAccess, updateStaffProfile, getTranscriptInbox, getTranscriptDetail, transcriptToVault, transcriptToStory, dismissTranscript, type InboxTranscript, getBackups, backupNow, downloadBackup, getEmailLog, sendTestEmail, getAdminWaiting, notifyArticleTeam, autowriteBacklog, type AdminWaitingItem, type AdminWaitingKind, getAiModels, setAiModel, resetAiModel, testAiModel, AI_EFFORTS, type AiTierRow, getSignalProbe, type SignalProbe, type AdminArticle, type AdminBlogTopic, type StaffMember, getBlogSettings, setBlogSettings, type BlogSettings, getDictationAdmin, getRollout, setRolloutValue, clearRolloutOverride } from '@/data/client';
import { Link, useNavigate } from 'react-router-dom';
import type { AdminClient } from '@/components/admin/types';
import { ConnChip } from '@/components/admin/ConnChip';
import { ReferralsBoard } from '@/components/admin/ReferralsBoard';
import { ReviewsWidgetBoard } from '@/components/admin/ReviewsWidgetBoard';
import { getAdminClients, adminClientPath, ADMIN_CLIENTS_KEY } from '@/components/admin/data';
import { apiErrMsg } from '@/components/admin/util';
import { DeleteClientModal } from '@/components/admin/DeleteClientModal';
/* ---- clients & connections tab --------------------------------------- */

function ClientsPanel() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useQuery({ queryKey: ADMIN_CLIENTS_KEY, queryFn: getAdminClients });

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<AdminClient | null>(null); // client pending deletion

  const [delErr, setDelErr] = useState<string | null>(null);
  const del = useMutation({
    mutationFn: (c: AdminClient) =>
      apiFetch(`/api/admin/clients/${encodeURIComponent(c.orgId)}`, { method: 'DELETE' }),
    onMutate: () => setDelErr(null),
    onSuccess: (_r, c) => {
      setToDelete(null);
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] });
      setSyncMsg(`Deleted “${c.name}” and all of its data.`);
    },
    // A failed delete used to close into nothing; the modal now says why.
    onError: (e) => setDelErr(apiErrMsg(e, 'Could not delete this client.')),
  });

  const runSync = async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await apiFetch<{ synced: { client: string; rows: number }[]; campaigns?: { client: string; campaigns: number }[]; campaignsError?: string }>('/api/admin/sync', { method: 'POST' });
      const total = res.synced.reduce((n, s) => n + s.rows, 0);
      const camp = (res.campaigns ?? []).reduce((n, s) => n + s.campaigns, 0);
      const campClients = (res.campaigns ?? []).length;
      const ads = res.campaignsError
        ? `Google Ads sync FAILED: ${res.campaignsError}${/invalid_grant/.test(res.campaignsError) ? ' — the refresh token was revoked; mint a new one with scripts/google-ads-token.mjs.' : ''}`
        : `Google Ads: ${camp} daily metric row(s) across ${campClients} client(s) with an Ads account.`;
      setSyncMsg(`Synced ${total} lead${total === 1 ? '' : 's'} across ${res.synced.length} client(s). ${ads}`);
    } catch (e) {
      setSyncMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncing(false);
    }
  };

  const makeDemo = useMutation({
    mutationFn: () => createDemoAccount(),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['admin', 'clients'] }); setSyncMsg(`Created demo account “${r.name}”. Switch to it in the org switcher; seed its data next.`); },
    onError: (e) => setSyncMsg(apiErrMsg(e, 'Could not create the demo account.')),
  });

  const clients = data?.clients ?? [];
  const managers = data?.managers ?? [];
  const hasDemo = clients.some((c) => c.isDemo);

  const [query, setQuery] = useState('');
  const visible = useMemo(
    () => findClients(clients, query, (id) => managers.find((m) => m.id === id)?.name),
    [clients, managers, query],
  );

  return (
    <div className="dash-stack">
      <Card variant="dark" padding="lg" className="cq-hero">
        <div className="cq-hero__txt">
          <span className="cq-hero__eyebrow"><Icon name="shield-check" size={14} /> Triton admin</span>
          <h2>Clients &amp; data connections</h2>
          <p>Assign each client its BigQuery table and Google Ads account, then sync.{data?.dataset ? ` Lead tables live under ${data.dataset}.` : ''}</p>
        </div>
        <Button variant="primary" iconLeft={<Icon name="trending-up" size={16} />} onClick={runSync} disabled={syncing}>
          {syncing ? 'Syncing…' : 'Sync all now'}
        </Button>
      </Card>

      {syncMsg && <Card padding="lg"><p className="dash-muted">{syncMsg}</p></Card>}

      <Card padding={false} className="dash-leads">
        <div className="dash-panelhead dash-panelhead--pad">
          <div>
            <h2>Clients</h2>
            <p>
              {query
                ? `${visible.length} of ${clients.length} client${clients.length === 1 ? '' : 's'} match “${query}”.`
                : 'Open a client to edit its data connections and business profile.'}
            </p>
          </div>
          <div className="admin-clients__tools">
            <div className="dash-search admin-search">
              <Icon name="search" size={16} />
              <input
                type="search"
                autoComplete="off"
                placeholder={clients.length ? `Search ${clients.length} clients…` : 'Search clients…'}
                aria-label="Search clients by name, manager, or account id"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button className="dash-search__clear" aria-label="Clear search" onClick={() => setQuery('')}>
                  <Icon name="x" size={14} />
                </button>
              )}
            </div>
            {!hasDemo && (
              <Button variant="ghost" size="sm" iconLeft={<Icon name="plus" size={15} />} disabled={makeDemo.isPending} onClick={() => makeDemo.mutate()}>
                {makeDemo.isPending ? 'Creating…' : 'Create demo account'}
              </Button>
            )}
          </div>
        </div>
        {isLoading ? (
          <div className="lead-none">Loading clients…</div>
        ) : isError ? (
          <div className="lead-none">Couldn’t load clients: {error instanceof Error ? error.message : 'error'}</div>
        ) : clients.length === 0 ? (
          <div className="lead-none">No client organizations yet.</div>
        ) : visible.length === 0 ? (
          <div className="lead-none">
            No client matches “{query}”.{' '}
            <button className="admin-clients__reset" onClick={() => setQuery('')}>Clear the search</button>
          </div>
        ) : (
          <table className="dash-table">
            <thead>
              <tr>
                <th>Client</th><th>Connections</th><th>Leads</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => {
                const open = () => navigate(adminClientPath(c.orgId));
                return (
                  /* The whole row opens the client; Settings and Legal buttons went
                     with it since both live on that page. Delete stays, and stops
                     the click so it never doubles as "open". */
                  <tr key={c.orgId} className="admin-row--link" role="link" tabIndex={0}
                    onClick={open}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}>
                    <td><div className="dash-cust"><Link to={adminClientPath(c.orgId)} className="admin-clientlink" onClick={(e) => e.stopPropagation()}>{c.name}</Link></div></td>
                    {/* At-a-glance summary of what is wired up. The editors live on
                        the client page, so this table cannot outgrow its width again
                        as new integrations are added. */}
                    <td>
                      <div className="admin-conns">
                        <ConnChip label="BigQuery" on={!!c.bqTable} detail={c.bqTable} />
                        {/* A failing sync used to show only as a stale date nobody
                            was watching — two clients sat unsynced for 11 days. */}
                        {c.leadsSyncError && (
                          <span className="admin-conn admin-conn--err" title={c.leadsSyncError}>
                            <Icon name="alert-circle" size={12} /> Lead sync failing
                            {c.leadsSyncErrorAt ? ` since ${c.leadsSyncErrorAt.slice(0, 10)}` : ''}
                          </span>
                        )}
                        {/* Shown so a client with no BigQuery table still reads as
                            Connected for a visible reason. */}
                        <ConnChip label="Direct capture" on={c.leadCaptureEnabled} detail={c.leadCaptureEnabled ? 'Wufoo / Formspree' : null} />
                        <ConnChip label="Google Ads" on={!!c.googleAdsCustomerId} detail={c.googleAdsCustomerId} />
                        <ConnChip label="GA4" on={!!c.ga4PropertyId} detail={c.ga4PropertyId} />
                        <ConnChip label="Search Console" on={!!c.gscSiteUrl} detail={c.gscSiteUrl} />
                        <ConnChip label="Reviews" on={!!c.gatherupClientId} detail={c.gatherupClientId} />
                        <ConnChip label="AI interview" on={c.aiInterviewEnabled} detail={c.aiInterviewEnabled ? c.interviewStyle : null} />
                        <ConnChip label="AI images" on={c.aiImagesEnabled} detail={null} />
                      </div>
                    </td>
                    <td className="dash-val">{c.leadCount.toLocaleString()}</td>
                    <td>{c.configured ? <Badge variant="success" dot>Connected</Badge> : <Badge variant="neutral">Not configured</Badge>}</td>
                    <td className="lead-td-actions" onClick={(e) => e.stopPropagation()}>
                      <div className="admin-rowactions">
                        <IconButton label={`Delete ${c.name}`} variant="ghost" onClick={() => setToDelete(c)}>
                          <Icon name="trash" size={16} />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {toDelete && (
        <DeleteClientModal
          client={toDelete}
          busy={del.isPending}
          error={delErr}
          onCancel={() => { setToDelete(null); setDelErr(null); }}
          onConfirm={() => del.mutate(toDelete)}
        />
      )}
    </div>
  );
}

/* Compact on/off pill summarising one integration in the clients table. The
   configured value is shown as a tooltip so the cell stays narrow. */
/* `source` says HOW a piece was made; this says WHO started it. A client
   self-serving an AI interview needs someone to act; the same interview run by
   Triton does not, and before this they looked identical. */
function OriginBadge({ origin, name }: { origin: 'client' | 'triton' | null; name?: string | null }) {
  if (!origin) return <span className="dash-muted" title="Created before origin was recorded">—</span>;
  const client = origin === 'client';
  return (
    <span title={name ? `Started by ${name}` : undefined}>
      <Badge variant={client ? 'accent' : 'neutral'}>{client ? 'Client' : 'Triton'}</Badge>
    </span>
  );
}

/* Admin used to render the raw stage, so an article already delivered to the
   client's site showed as "Draft" here and "Pushed to site" in contentIQ — the
   two screens disagreeing about the same row. Both now read one helper. */
function AdminStatusPill({ stage, pushedAt }: { stage: number; pushedAt?: string | null }) {
  const key = articleStatus({ stage, pushedAt: pushedAt ?? undefined });
  return (
    <span title={ARTICLE_STATUS_HINT[key]}>
      <Badge variant={ARTICLE_STATUS_VARIANT[key]} dot={key === 'draft' || key === 'review' || key === 'interview'}>
        {ARTICLE_STATUS_LABEL[key]}
      </Badge>
    </span>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}
function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86_400_000);
}

/* Every client we are waiting on for answers. Oldest first, because the one
   that has sat longest is the one to raise on the next call. "Remind now" is
   the weekly nudge on demand; "Write with what we have" stops waiting. */
function OutstandingQuestions() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin', 'gaps', 'outstanding'], queryFn: getOutstandingGaps });
  const [busy, setBusy] = useState<string | null>(null);
  const items = data?.items ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin', 'gaps', 'outstanding'] });
  const act = async (id: string, fn: () => Promise<unknown>) => { setBusy(id); try { await fn(); refresh(); } finally { setBusy(null); } };
  if (!items.length) return null;
  const ageDays = (iso: string) => daysSince(iso) ?? 0;
  return (
    <CollapsibleSection title="Client questions outstanding" padding="lg" className="cq-card"
      subtitle={`${items.length} set${items.length === 1 ? '' : 's'} across ${new Set(items.map((i) => i.clientId)).size} client${new Set(items.map((i) => i.clientId)).size === 1 ? '' : 's'} · reminded weekly, three times at most`}>
      <div style={{ overflowX: 'auto' }}>
        <table className="dash-table">
          <thead><tr><th>Client</th><th>Topic</th><th>Sent</th><th>Answered</th><th>Reminders</th><th></th></tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.articleId}>
                <td><strong>{s.clientName}</strong></td>
                <td>{s.short}<div className="dash-muted" style={{ fontSize: 'var(--text-xs)', maxWidth: 360 }}>{s.title}{s.sentBy ? ` · sent by ${s.sentBy}` : ''}</div></td>
                <td style={{ whiteSpace: 'nowrap' }}>{s.sentAt.slice(0, 10)}<div className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{ageDays(s.sentAt)}d ago</div></td>
                <td>{s.answered} of {s.total}{s.declined ? ` · ${s.declined} skipped` : ''}{s.lastActivity ? <div className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>last {s.lastActivity.slice(0, 10)}</div> : <div className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>no answers yet</div>}</td>
                <td>{s.reminders}{s.remindedAt ? <div className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>last {s.remindedAt.slice(0, 10)}</div> : null}{s.reminders >= 3 && <div className="admin-modal__err" style={{ fontSize: 'var(--text-xs)' }}>no more automatic reminders</div>}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <Button size="sm" variant="outline" disabled={busy === s.articleId} onClick={() => act(s.articleId, () => remindGapSetNow(s.articleId))}>Remind now</Button>{' '}
                  <Button size="sm" variant="ghost" disabled={busy === s.articleId} onClick={() => { if (window.confirm(`Stop waiting on ${s.clientName}? Unanswered questions are marked "no response" and the writer gets what was answered.`)) void act(s.articleId, () => closeGapSetNow(s.articleId)); }}>Write with what we have</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleSection>
  );
}

/* Blog topics awaiting approval, grouped by the batch they were requested in
   (one batch = one page's supporting topics). "Mine" shows the batches on
   clients where the viewer is the Optimization Manager (approver), Content
   Manager or contentIQ Editor; it is the default because the SEO person's
   question is "what is waiting on me", not "what is waiting on everyone". A
   viewer with no such role on any client sees everything. */
type TopicSort = 'newest' | 'oldest' | 'client';
function BlogApprovalBoard({ topics, viewerId, initialBatch, onChanged }: { topics: AdminBlogTopic[]; viewerId: string; initialBatch: string; onChanged: () => void }) {
  const pending = useMemo(() => topics.filter((t) => t.status === 'pending'), [topics]);
  const mineCount = useMemo(() => pending.filter((t) => [t.optimizationManagerId, t.contentManagerId, t.editorId].includes(viewerId)).length, [pending, viewerId]);
  const hasRole = useMemo(() => topics.some((t) => [t.optimizationManagerId, t.contentManagerId, t.editorId].includes(viewerId)), [topics, viewerId]);
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [client, setClient] = useState('all');
  const [page, setPage] = useState('all');
  const [sort, setSort] = useState<TopicSort>('newest');
  const [batch, setBatch] = useState(initialBatch);
  const clients = useMemo(() => [...new Map(pending.map((t) => [t.clientId, t.clientName])).entries()].sort((a, b) => a[1].localeCompare(b[1])), [pending]);
  const pagesFor = useMemo(() => [...new Set(pending.filter((t) => client === 'all' || t.clientId === client).map((t) => t.supportAnchor || t.supportUrl).filter(Boolean))].sort(), [pending, client]);

  const rows = useMemo(() => pending.filter((t) =>
    (batch ? t.batchId === batch : true)
    && (scope === 'all' || !hasRole || [t.optimizationManagerId, t.contentManagerId, t.editorId].includes(viewerId))
    && (client === 'all' || t.clientId === client)
    && (page === 'all' || (t.supportAnchor || t.supportUrl) === page)), [pending, batch, scope, hasRole, viewerId, client, page]);

  const batches = useMemo(() => {
    const m = new Map<string, AdminBlogTopic[]>();
    for (const t of rows) { const k = t.batchId || `single:${t.id}`; const arr = m.get(k); if (arr) arr.push(t); else m.set(k, [t]); }
    const list = [...m.values()];
    list.sort((a, b) => sort === 'client' ? a[0].clientName.localeCompare(b[0].clientName) || b[0].createdAt.localeCompare(a[0].createdAt)
      : sort === 'oldest' ? a[0].createdAt.localeCompare(b[0].createdAt) : b[0].createdAt.localeCompare(a[0].createdAt));
    return list;
  }, [rows, sort]);

  if (!pending.length) return null;
  return (
    <CollapsibleSection title="Blog topics awaiting approval" padding="lg" defaultOpen={!!initialBatch}
      subtitle={`${rows.length} of ${pending.length} pending · the Optimization Manager approves, then it goes to the contentIQ Editor (or Content Manager) to write`}
      right={
        <div className="lead-toolbar__filters">
          {hasRole && (
            <label className="lead-field"><Icon name="users" size={15} />
              <select className="tr-select" value={scope} onChange={(e) => setScope(e.target.value as 'mine' | 'all')} aria-label="Assigned to">
                <option value="mine">Assigned to me ({mineCount})</option>
                <option value="all">Everyone ({pending.length})</option>
              </select>
            </label>
          )}
          <label className="lead-field"><Icon name="home" size={15} />
            <select className="tr-select" value={client} onChange={(e) => { setClient(e.target.value); setPage('all'); }} aria-label="Client">
              <option value="all">All clients</option>
              {clients.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </label>
          {pagesFor.length > 0 && (
            <label className="lead-field"><Icon name="link" size={15} />
              <select className="tr-select" value={page} onChange={(e) => setPage(e.target.value)} aria-label="Supported page">
                <option value="all">All pages</option>
                {pagesFor.map((pg) => <option key={pg} value={pg}>{pg}</option>)}
              </select>
            </label>
          )}
          <label className="lead-field"><Icon name="calendar" size={15} />
            <select className="tr-select" value={sort} onChange={(e) => setSort(e.target.value as TopicSort)} aria-label="Sort">
              <option value="newest">Newest requested</option>
              <option value="oldest">Oldest requested</option>
              <option value="client">By client</option>
            </select>
          </label>
          {batch && <Button size="sm" variant="ghost" onClick={() => setBatch('')}>Show all batches</Button>}
        </div>
      }>
      {batches.length === 0
        ? <p className="dash-muted">{scope === 'mine' && hasRole ? 'Nothing is waiting on you. Switch to Everyone to see the rest.' : 'Nothing matches these filters.'}</p>
        : batches.map((group) => <BlogApprovalBatch key={group[0].batchId || group[0].id} group={group} onChanged={onChanged} />)}
    </CollapsibleSection>
  );
}

function BlogApprovalBatch({ group, onChanged }: { group: AdminBlogTopic[]; onChanged: () => void }) {
  const head = group[0];
  const [busy, setBusy] = useState(false);
  const isBatch = !!head.batchId && group.length > 0;
  const act = async (status: 'approved' | 'rejected') => {
    if (!window.confirm(`${status === 'approved' ? 'Approve' : 'Reject'} all ${group.length} pending topic${group.length === 1 ? '' : 's'} in this batch?`)) return;
    setBusy(true);
    try { await setAdminBlogTopicBatchStatus(head.batchId, status); onChanged(); } finally { setBusy(false); }
  };
  return (
    <div className="ba-batch">
      <div className="ba-batch__head">
        <span className="ba-batch__title">{head.supportUrl ? <>Supports: <a href={head.supportUrl} target="_blank" rel="noreferrer">{head.supportAnchor || head.supportUrl}</a></> : 'Single topic'}</span>
        <span className="ba-batch__meta">
          {head.clientName}{head.requestedBy ? ` · requested by ${head.requestedBy}` : ''} · {head.createdAt.slice(0, 10)} · {group.length} topic{group.length === 1 ? '' : 's'}
          {' · '}approve: <strong>{head.optimizationManagerName || 'Optimization Mgr (unassigned)'}</strong> → write: <strong>{head.editorName ? <>{head.editorName} (<IqName text="contentIQ" /> Editor)</> : head.contentManagerName || 'Content Mgr (unassigned)'}</strong>
        </span>
        {isBatch && group.length > 1 && (
          <div className="ba-batch__actions">
            <Button variant="primary" size="sm" disabled={busy} onClick={() => act('approved')}>Approve batch</Button>
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => act('rejected')}>Reject batch</Button>
          </div>
        )}
      </div>
      <div className="ba-list">
        {group.map((t) => <BlogApprovalRow key={t.id} t={t} onChanged={onChanged} />)}
      </div>
    </div>
  );
}

function BlogApprovalRow({ t, onChanged }: { t: AdminBlogTopic; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [topic, setTopic] = useState(t.topic);
  const [qs, setQs] = useState<string[]>(t.questions.length ? t.questions : ['']);
  const [kw, setKw] = useState(t.keywords);
  const dirty = topic !== t.topic || kw !== t.keywords || JSON.stringify(qs.filter((q) => q.trim())) !== JSON.stringify(t.questions);
  const save = async () => {
    setBusy(true);
    try { await updateAdminBlogTopic(t.id, { topic, questions: qs.filter((q) => q.trim()), keywords: kw }); onChanged(); } finally { setBusy(false); }
  };
  /* Approving saves any edit first: the writer must get the version the
     reviewer actually approved, not the one the requester sent. */
  const act = async (status: 'approved' | 'rejected') => {
    setBusy(true);
    try {
      if (dirty && status === 'approved') await updateAdminBlogTopic(t.id, { topic, questions: qs.filter((q) => q.trim()), keywords: kw });
      await setAdminBlogTopicStatus(t.id, status); onChanged();
    } finally { setBusy(false); }
  };
  return (
    <div className="ba-item">
      <div className="ba-head">
        <button type="button" className="ba-toggle" onClick={() => setOpen((o) => !o)}>
          <Icon name={open ? 'chevron-down' : 'chevron-right'} size={13} />
          <span className="ba-topic">{topic}</span>
        </button>
        <span className="ba-meta">{t.questions.length} sub-topic{t.questions.length === 1 ? '' : 's'}{t.keywords ? ` · ${t.keywords}` : ''}{dirty ? ' · edited' : ''}</span>
        <div className="ba-actions">
          <Button variant="primary" size="sm" disabled={busy} onClick={() => act('approved')}>{dirty ? 'Save & approve' : 'Approve'}</Button>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => act('rejected')}>Reject</Button>
        </div>
      </div>
      {open && (
        <div className="ba-body ba-edit">
          <Input label="Topic" value={topic} onChange={(e) => setTopic(e.target.value)} />
          {qs.map((q, i) => (
            <div key={i} className="ba-edit__row">
              <Input value={q} onChange={(e) => setQs(qs.map((v, j) => (j === i ? e.target.value : v)))} placeholder={`Sub-topic ${i + 1}`} />
              <IconButton label="Remove sub-topic" onClick={() => setQs(qs.filter((_, j) => j !== i))}><Icon name="trash" size={14} /></IconButton>
            </div>
          ))}
          <button type="button" className="bq-add" onClick={() => setQs([...qs, ''])}>+ sub-topic</button>
          <Input label="Target keywords" hint="For topic selection — the writer keeps the prose natural." value={kw} onChange={(e) => setKw(e.target.value)} />
          {dirty && <div><Button size="sm" variant="outline" disabled={busy} onClick={save}>Save edits</Button></div>}
        </div>
      )}
    </div>
  );
}

// Collapsed-by-default section: a clickable card header (chevron + title) that
// reveals its body only when opened — so the Content tab shows a short menu of
// reports and the visitor expands just the one they need.
function CollapsibleSection({ title, subtitle, defaultOpen = false, padding = false, className = 'dash-leads', right, children }: {
  title: string; subtitle?: ReactNode; defaultOpen?: boolean;
  padding?: 'lg' | false; className?: string; right?: ReactNode; children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const headerPadded = padding === false; // pad the header only when the card itself has none
  return (
    <Card padding={padding} className={className}>
      <div className={`dash-panelhead${headerPadded ? ' dash-panelhead--pad' : ''}`} role="button" tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
        style={{ cursor: 'pointer' }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name={open ? 'chevron-down' : 'chevron-right'} size={16} /> {title}
          </h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {open && right && <div onClick={(e) => e.stopPropagation()}>{right}</div>}
      </div>
      {open && children}
    </Card>
  );
}

const MATRIX_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Coverage grid: clients × months, counting published articles by publish date.
// Mirrors the master content tracker so gaps are obvious. A month with 0 that is
// already due (this month or earlier) is flagged as a "hole"; future months read
// as neutral (not yet written).
function CoverageMatrix() {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState<number | undefined>(undefined);
  // Collapsed by default and only fetched once opened — with 100+ clients this
  // grid is tall, so it shouldn't auto-expand when the Content tab loads.
  const { data, isLoading, isError } = useQuery({ queryKey: ['admin', 'contentMatrix', year], queryFn: () => getContentMatrix(year), enabled: open });
  const now = new Date();
  const shownYear = data?.year ?? now.getUTCFullYear();
  const dueThrough = shownYear < now.getUTCFullYear() ? 11 : shownYear > now.getUTCFullYear() ? -1 : now.getUTCMonth();

  const th: CSSProperties = { padding: '6px 8px', borderBottom: '2px solid var(--border, #e5e7eb)', fontSize: 'var(--text-xs)', fontWeight: 600, textAlign: 'center', whiteSpace: 'nowrap' };
  const td: CSSProperties = { padding: '5px 8px', borderBottom: '1px solid var(--border, #eef0f3)', fontSize: 'var(--text-xs)', textAlign: 'center', minWidth: 34 };

  return (
    <Card padding={false} className="dash-leads">
      <div className="dash-panelhead dash-panelhead--pad" role="button" tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
        style={{ cursor: 'pointer' }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name={open ? 'chevron-down' : 'chevron-right'} size={16} /> Content coverage
          </h2>
          <p>Published articles per client, by month{open ? ' — red = a due month with a gap' : ' · click to open'}</p>
        </div>
        {open && (
          <label className="lead-field" onClick={(e) => e.stopPropagation()}><Icon name="calendar" size={15} />
            <select className="tr-select" value={shownYear} onChange={(e) => setYear(Number(e.target.value))} aria-label="Year">
              {(data?.years ?? [String(shownYear)]).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
        )}
      </div>
      {open && (isLoading ? <p style={{ padding: 16 }} className="dash-muted">Loading…</p>
        : isError || !data ? <p style={{ padding: 16 }} className="err">Could not load coverage.</p>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: 'left', position: 'sticky', left: 0, background: 'var(--surface, #fff)' }}>Client</th>
                    {MATRIX_MONTHS.map((m) => <th key={m} style={th}>{m}</th>)}
                    <th style={th}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.clients.map((cl) => (
                    <tr key={cl.orgId}>
                      <td style={{ ...td, textAlign: 'left', whiteSpace: 'nowrap', position: 'sticky', left: 0, background: 'var(--surface, #fff)' }}>{cl.name}</td>
                      {cl.months.map((n, i) => {
                        const hole = n === 0 && i <= dueThrough;
                        return (
                          <td key={i} style={{ ...td, background: n > 0 ? '#fdf3d7' : hole ? '#fdecec' : undefined, color: hole ? '#b91c1c' : undefined, fontWeight: n > 0 ? 600 : 400 }}>
                            {n > 0 ? n : hole ? '—' : ''}
                          </td>
                        );
                      })}
                      <td style={{ ...td, fontWeight: 700 }}>{cl.total || ''}</td>
                    </tr>
                  ))}
                  {data.clients.length === 0 && (
                    <tr><td style={{ ...td, textAlign: 'left' }} colSpan={14}>No clients yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ))}
    </Card>
  );
}

// One transcript in the inbox: pick a client, then pull into their Vault and/or a
// Story. Attached rows show where they went. Full text expands on demand.
function TranscriptRow({ item, clients, onChanged }: {
  item: InboxTranscript; clients: { orgId: string; name: string }[]; onChanged: () => void;
}) {
  const [orgId, setOrgId] = useState('');
  const [title, setTitle] = useState(item.title);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const { data: detail } = useQuery({ queryKey: ['admin', 'transcript', item.id], queryFn: () => getTranscriptDetail(item.id), enabled: open });

  const when = new Date(item.startedAt);
  const whenLabel = isNaN(when.getTime()) ? '' : when.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const who = item.participants.map((p) => p.name || p.email).filter(Boolean).join(', ');

  const run = async (kind: string, fn: () => Promise<unknown>) => {
    setBusy(kind);
    try { await fn(); onChanged(); } finally { setBusy(null); }
  };

  return (
    <div className="admin-transcript">
      <div className="admin-transcript__head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong>{item.title}</strong>
          <p className="dash-muted" style={{ margin: '2px 0 0' }}>{whenLabel}{who ? ` · ${who}` : ''}</p>
          <p className="dash-muted" style={{ margin: '4px 0 0', fontSize: 'var(--text-xs)' }}>{item.preview}…</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setOpen((o) => !o)}>{open ? 'Hide' : 'View'}</Button>
      </div>
      {open && (
        <pre className="admin-transcript__text">{detail ? detail.text : 'Loading…'}</pre>
      )}
      <div className="admin-transcript__actions">
        <select className="tr-select" value={orgId} onChange={(e) => setOrgId(e.target.value)} aria-label="Client">
          <option value="">Choose client…</option>
          {clients.map((c) => <option key={c.orgId} value={c.orgId}>{c.name}</option>)}
        </select>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" aria-label="Story title" />
        <Button variant="outline" size="sm" disabled={!orgId || !!busy}
          onClick={() => run('vault', () => transcriptToVault(item.id, orgId))}>
          {busy === 'vault' ? 'Adding…' : 'To Vault'}
        </Button>
        <Button variant="primary" size="sm" disabled={!orgId || !!busy}
          onClick={() => run('story', () => transcriptToStory(item.id, orgId, title))}>
          {busy === 'story' ? 'Creating…' : 'To Story'}
        </Button>
        <Button variant="ghost" size="sm" disabled={!!busy}
          onClick={() => { if (confirm('Dismiss this transcript?')) run('dismiss', () => dismissTranscript(item.id)); }}>
          Dismiss
        </Button>
      </div>
    </div>
  );
}

function TranscriptInbox({ clients }: { clients: { orgId: string; name: string }[] }) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({ queryKey: ['admin', 'transcriptInbox'], queryFn: getTranscriptInbox });
  const items = data?.items ?? [];
  const newCount = items.filter((i) => i.status === 'new').length;
  const onChanged = () => qc.invalidateQueries({ queryKey: ['admin', 'transcriptInbox'] });

  return (
    <CollapsibleSection title="Meeting transcripts" padding="lg" className="cq-card"
      subtitle={newCount ? `${newCount} new from Roam Magic Minutes — pull into a Vault or Story` : 'Recent Magic Minutes transcripts (incl. unscheduled kickoff / pre-sale calls)'}>
      {isLoading ? <p className="dash-muted">Loading…</p>
        : isError ? <p className="err">Could not load transcripts.</p>
          : items.length === 0 ? <p className="dash-muted">No recent transcripts. Meetings recorded with Roam Magic Minutes show up here.</p>
            : (
              <div className="admin-transcript-list">
                {items.map((it) => <TranscriptRow key={it.id} item={it} clients={clients} onChanged={onChanged} />)}
              </div>
            )}
    </CollapsibleSection>
  );
}

/* What is waiting on the team, on every visit. The weekly email is the
   backstop; this is the same queues, live, led by the viewer's own. Rows
   carry how long the oldest item has waited and since when, because "3
   interviews" reads very differently at 2 days and at 40. */
const WAITING_HEADING: Record<AdminWaitingKind, string> = {
  interview_review: 'AI interviews to write up',
  draft_review: 'Drafts to review and push',
  interview_abandoned: 'Interviews started and never finished',
  faq_review: 'Client FAQs to review',
  topic_approval: 'Blog topics to approve',
  stale_questions: 'Clients who have stopped replying',
  expert_review: 'Expert profiles to proofread',
  unattributed: 'Work with nobody credited',
  published: 'Went live this week',
};
const WAITING_ORDER = Object.keys(WAITING_HEADING) as AdminWaitingKind[];
/* Where to go and look, per kind; the content kinds open that client's library. */
const WAITING_PATH: Partial<Record<AdminWaitingKind, string>> = {
  interview_review: '/content', draft_review: '/content', interview_abandoned: '/content',
  stale_questions: '/content', unattributed: '/content', faq_review: '/faq', expert_review: '/experts',
};

function WaitingPanel() {
  const qc = useQueryClient();
  const { organization } = useOrganization();
  const { setActive } = useOrganizationList();
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'waiting'], queryFn: getAdminWaiting, staleTime: 2 * 60_000 });
  const items = data?.items ?? [];
  const me = (data?.me ?? '').toLowerCase();
  const mine = items.filter((i) => i.owner?.email.toLowerCase() === me);
  const [scope, setScope] = useState<'mine' | 'all' | null>(null);
  const effective = scope ?? (mine.length ? 'mine' : 'all');
  const shown = (effective === 'mine' ? mine : items).filter((i) => i.kind !== 'published');
  const queued = shown.length;
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const open = async (i: AdminWaitingItem) => {
    const path = WAITING_PATH[i.kind];
    if (!path) return;
    if (organization?.id !== i.clientId && setActive) { try { await setActive({ organization: i.clientId }); } catch { /* navigate anyway */ } }
    window.location.assign(path);
  };

  /* Dry run first, so the confirm names the real count; the write itself is
     two jobs per five minutes from there, each an email when it lands. */
  const writeBacklog = async () => {
    setBusy(true); setNote(null);
    try {
      const dry = await autowriteBacklog(true);
      if (!dry.candidates.length) { setNote('Nothing to write up — every finished interview already has a draft.'); return; }
      const blogs = dry.candidates.filter((c) => c.writes === 'blog').length;
      const ok = window.confirm(`Write up ${dry.candidates.length} finished interview${dry.candidates.length === 1 ? '' : 's'} with the auto-writer — ${blogs} as blog${blogs === 1 ? '' : 's'}, ${dry.candidates.length - blogs} as case stud${dry.candidates.length - blogs === 1 ? 'y' : 'ies'}? Two are written every five minutes; whoever owns each client gets an email as each one lands.`);
      if (!ok) return;
      const r = await autowriteBacklog(false);
      setNote(`${r.queued} queued. The first drafts land within five minutes; all of them in about ${Math.ceil((r.queued / 2) * 5)} minutes.`);
      qc.invalidateQueries({ queryKey: ['admin', 'waiting'] });
    } catch (e) { setNote(apiErrMsg(e, 'Could not queue the backlog.')); }
    finally { setBusy(false); }
  };

  const groups = WAITING_ORDER.map((kind) => ({ kind, rows: shown.filter((i) => i.kind === kind).sort((a, b) => b.days - a.days) })).filter((g) => g.rows.length);
  const oldest = shown.length ? Math.max(...shown.map((i) => i.days)) : 0;

  return (
    <CollapsibleSection
      title="Waiting on the team" padding="lg" className="cq-card"
      subtitle={isLoading ? 'Checking every queue…' : queued ? `${queued} thing${queued === 1 ? '' : 's'} waiting${oldest >= 7 ? ` · the oldest for ${oldest} days` : ''}` : 'Nothing is waiting. 🎉'}
      right={
        <div className="lead-toolbar__filters">
          {me && (
            <label className="lead-field"><Icon name="users" size={15} />
              <select className="tr-select" value={effective} onChange={(e) => setScope(e.target.value as 'mine' | 'all')} aria-label="Whose queue">
                <option value="mine">Mine ({mine.filter((i) => i.kind !== 'published').length})</option>
                <option value="all">Everyone ({items.filter((i) => i.kind !== 'published').length})</option>
              </select>
            </label>
          )}
          {items.some((i) => i.kind === 'interview_review') && (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => void writeBacklog()} iconLeft={<Icon name="sparkles" size={14} />}>
              {busy ? 'Working…' : 'Write up the backlog'}
            </Button>
          )}
        </div>
      }
    >
      {note && <p className="dash-muted" style={{ marginTop: 0 }}>{note}</p>}
      {!isLoading && groups.length === 0 ? null : (
        <div className="admin-waiting">
          {groups.map((g) => (
            <div key={g.kind} className="admin-waiting__group">
              <h4 className="admin-waiting__head">{WAITING_HEADING[g.kind]} <span className="dash-muted">{g.rows.length}</span></h4>
              <ul className="admin-waiting__list">
                {g.rows.map((i, n) => (
                  <li key={`${g.kind}-${i.clientId}-${n}`} className={`admin-waiting__row${WAITING_PATH[i.kind] ? ' is-link' : ''}`} onClick={() => void open(i)} role={WAITING_PATH[i.kind] ? 'link' : undefined} tabIndex={WAITING_PATH[i.kind] ? 0 : undefined}>
                    <strong>{i.clientName}</strong>
                    <span className="admin-waiting__label">{i.url ? <a href={i.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{i.label}</a> : i.label}</span>
                    <span className={`admin-waiting__age${i.days >= 14 ? ' is-old' : i.days >= 7 ? ' is-aging' : ''}`} title={i.since ? `Since ${fmtDate(i.since)}` : undefined}>
                      {i.days === 0 ? 'today' : `${i.days}d`}{i.since ? ` · since ${fmtDate(i.since)}` : ''}
                    </span>
                    {effective === 'all' && <em className="admin-waiting__owner">{i.owner ? i.owner.name || i.owner.email : 'Unassigned'}</em>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
}

function ContentPanel() {
  const qc = useQueryClient();
  const { organization } = useOrganization();
  const { setActive } = useOrganizationList();
  const { data, isLoading, isError } = useQuery({ queryKey: ['admin', 'content'], queryFn: getAdminContent });
  const { data: btData } = useQuery({ queryKey: ['admin', 'blogTopics'], queryFn: getAdminBlogTopics });
  // Open an article in the contentIQ viewer at its current step. The viewer is
  // per-client, so switch to that client's org first (a reload lands on it).
  const openArticle = async (a: AdminArticle) => {
    const target = `/content?article=${encodeURIComponent(a.id)}`;
    if (organization?.id === a.clientId || !setActive) { window.location.assign(target); return; }
    try { await setActive({ organization: a.clientId }); } catch { /* fall through to navigate */ }
    window.location.assign(target);
  };
  const [status, setStatus] = useState('all');
  const [source, setSource] = useState('all');
  const [origin, setOrigin] = useState('all');
  /* Hidden by default, not dropped: a client who starts an AI interview and
     walks away is worth being able to look at — several on one account means
     the interview is losing people, which only shows if the rows survive. */
  const [showAbandoned, setShowAbandoned] = useState(false);
  const [client, setClient] = useState('all');
  const [manager, setManager] = useState('all');
  const [sort, setSort] = useState<'created' | 'published'>('created');
  const [staleDays, setStaleDays] = useState(30);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);

  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusyId(id);
    try { await fn(); qc.invalidateQueries({ queryKey: ['admin', 'content'] }); } finally { setBusyId(null); setConfirmDel(null); }
  };

  const refetchTopics = () => qc.invalidateQueries({ queryKey: ['admin', 'blogTopics'] });
  /* ?batch=<id> is the link in the "topics to approve" email: open the board on
     that batch alone. Read once; the board's own controls take over after. */
  const [initialBatch] = useState(() => new URLSearchParams(window.location.search).get('batch') || '');
  const setRejected = (id: string, rejected: boolean) =>
    act(id, () => apiFetch(`/api/admin/content/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ rejected }) }));
  const remove = (id: string) =>
    act(id, () => apiFetch(`/api/admin/content/${encodeURIComponent(id)}`, { method: 'DELETE' }));
  const notify = (id: string) => act(id, () => notifyArticleTeam(id));

  /* Counted across everything, not the filtered set: the point is to say what
     the default is holding back, which a filtered count could not. */
  const abandonedCount = useMemo(
    () => (data?.articles ?? []).filter((a) => !a.rejected
      && isAbandonedInterview({ source: a.source, stage: a.stage, createdAt: a.createdAt })).length,
    [data],
  );

  /* An interview and the article written from it are one piece of work — the
     same fold contentIQ's library applies, so both screens list the same
     things. Only live articles fold anything: a rejected one leaves its
     interview in view, exactly as deleting it would in contentIQ. */
  const superseded = useMemo(
    () => supersededStoryIds((data?.articles ?? []).filter((a) => !a.rejected)),
    [data],
  );

  const articles = useMemo(() => {
    let out = (data?.articles ?? []).filter(
      (a) =>
        !superseded.has(a.id) &&
        (status === 'all'
          ? true
          : status === 'rejected'
            ? a.rejected
            : !a.rejected && articleStatus({ stage: a.stage, pushedAt: a.pushedAt ?? undefined }) === status) &&
        (source === 'all' || a.source === source) &&
        (origin === 'all' || (origin === 'unknown' ? !a.origin : a.origin === origin)) &&
        (showAbandoned || !isAbandonedInterview({ source: a.source, stage: a.stage, createdAt: a.createdAt })) &&
        (client === 'all' || a.clientId === client) &&
        (manager === 'all' || (manager === 'unassigned' ? !a.managerId : a.managerId === manager)),
    );
    out = [...out].sort((a, b) => {
      const av = sort === 'published' ? a.publishedAt ?? '' : a.createdAt;
      const bv = sort === 'published' ? b.publishedAt ?? '' : b.createdAt;
      return bv.localeCompare(av);
    });
    return out;
  }, [data, superseded, status, source, origin, showAbandoned, client, manager, sort]);

  const staleClients = useMemo(
    () =>
      (data?.clients ?? [])
        .map((c) => ({ ...c, days: daysSince(c.lastInterviewAt) }))
        .filter((c) => c.days === null || c.days >= staleDays)
        .sort((a, b) => (b.days ?? 1e9) - (a.days ?? 1e9)),
    [data, staleDays],
  );

  return (
    <div className="dash-stack">
      <WaitingPanel />

      <OutstandingQuestions />

      <CoverageMatrix />

      <TranscriptInbox clients={data?.clients ?? []} />

      <CollapsibleSection title="Follow-up radar" padding="lg" className="cq-card" subtitle="Clients with no recent interview">
        <div className="admin-stale">
          <label className="admin-stale__days">
            No interview in
            <input type="number" min={1} value={staleDays} onChange={(e) => setStaleDays(Math.max(1, Number(e.target.value) || 30))} />
            days
          </label>
          {staleClients.length === 0 ? (
            <span className="dash-muted">Every client has been interviewed within {staleDays} days. 🎉</span>
          ) : (
            <div className="admin-stale__list">
              {staleClients.map((c) => (
                <span key={c.orgId} className="admin-stale__chip">
                  {c.name} · {c.days === null ? 'never' : `${c.days}d ago`}
                </span>
              ))}
            </div>
          )}
        </div>
      </CollapsibleSection>

      <BlogApprovalBoard topics={btData?.topics ?? []} viewerId={btData?.viewerId ?? ''} initialBatch={initialBatch} onChanged={refetchTopics} />

      <CollapsibleSection title="All content" padding={false} className="dash-leads"
        subtitle={`${articles.length} item${articles.length !== 1 ? 's' : ''} across all clients${abandonedCount && !showAbandoned ? ` · ${abandonedCount} unfinished interview${abandonedCount === 1 ? '' : 's'} hidden` : ''}`}
        right={
          <div className="lead-toolbar__filters">
            <label className="lead-field"><Icon name="filter" size={15} />
              <select className="tr-select" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
                <option value="all">All statuses</option>
                {ARTICLE_STATUS_ORDER.map((k) => <option key={k} value={k}>{ARTICLE_STATUS_LABEL[k]}</option>)}
                <option value="rejected">Rejected</option>
              </select>
            </label>
            {abandonedCount > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setShowAbandoned((v) => !v)}>
                {showAbandoned ? 'Hide' : 'Show'} unfinished
              </Button>
            )}
            <label className="lead-field"><Icon name="users" size={15} />
              <select className="tr-select" value={origin} onChange={(e) => setOrigin(e.target.value)} aria-label="Started by">
                <option value="all">Anyone</option>
                <option value="client">Client-started</option>
                <option value="triton">Triton-started</option>
                <option value="unknown">Unknown</option>
              </select>
            </label>
            <label className="lead-field"><Icon name="sparkles" size={15} />
              <select className="tr-select" value={source} onChange={(e) => setSource(e.target.value)} aria-label="Source">
                <option value="all">All sources</option>
                <option value="ai">AI interview</option>
                <option value="human">In-person</option>
              </select>
            </label>
            <label className="lead-field"><Icon name="users" size={15} />
              <select className="tr-select" value={client} onChange={(e) => setClient(e.target.value)} aria-label="Client">
                <option value="all">All clients</option>
                {(data?.clients ?? []).map((c) => <option key={c.orgId} value={c.orgId}>{c.name}</option>)}
              </select>
            </label>
            <label className="lead-field"><Icon name="shield-check" size={15} />
              <select className="tr-select" value={manager} onChange={(e) => setManager(e.target.value)} aria-label="Content manager">
                <option value="all">All managers</option>
                <option value="unassigned">Unassigned</option>
                {(data?.managers ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </label>
            <label className="lead-field"><Icon name="calendar" size={15} />
              <select className="tr-select" value={sort} onChange={(e) => setSort(e.target.value as 'created' | 'published')} aria-label="Sort">
                <option value="created">Newest created</option>
                <option value="published">Recently published</option>
              </select>
            </label>
          </div>
        }
      >
        {isLoading ? (
          <div className="lead-none">Loading content…</div>
        ) : isError ? (
          <div className="lead-none">Couldn’t load content.</div>
        ) : articles.length === 0 ? (
          <div className="lead-none">No content matches these filters.</div>
        ) : (
          <table className="dash-table">
            <thead>
              <tr><th>Client</th><th>Manager</th><th>Title</th><th>Type</th><th>Started by</th><th>Source</th><th>Status</th><th>Words</th><th>Created</th><th>Published</th><th></th></tr>
            </thead>
            <tbody>
              {articles.map((a: AdminArticle) => (
                <tr key={a.id} className={`cq-item--clickable ${a.rejected ? 'admin-row--rejected' : ''}`.trim()} onClick={() => openArticle(a)}>
                  <td className="dash-cust">{a.clientName}</td>
                  <td className="dash-muted">{a.managerName || 'Unassigned'}</td>
                  <td>{a.title}</td>
                  <td className="dash-muted">{a.type || '—'}</td>
                  <td><OriginBadge origin={a.origin} name={a.createdByName} /></td>
                  <td><Badge variant={a.source === 'ai' ? 'accent' : 'neutral'}>{a.source === 'ai' ? 'AI' : 'In-person'}</Badge></td>
                  <td>{a.rejected ? <Badge variant="outline">Rejected</Badge> : <AdminStatusPill stage={a.stage} pushedAt={a.pushedAt} />}</td>
                  <td className="dash-val">{a.words ? a.words.toLocaleString() : '—'}</td>
                  <td className="dash-time">{fmtDate(a.createdAt)}</td>
                  <td className="dash-time">{fmtDate(a.publishedAt)}</td>
                  <td className="lead-td-actions" onClick={(e) => e.stopPropagation()}>
                    <div className="admin-rowactions">
                      {!a.rejected && a.source === 'ai' && a.stage === 1 && !a.pushedAt && (
                        <Button variant="ghost" size="sm" disabled={busyId === a.id} onClick={() => notify(a.id)} title="Send the interview-complete email again, to whoever it routes to today">Notify team</Button>
                      )}
                      {a.rejected
                        ? <Button variant="ghost" size="sm" disabled={busyId === a.id} onClick={() => setRejected(a.id, false)}>Restore</Button>
                        : <Button variant="ghost" size="sm" disabled={busyId === a.id} onClick={() => setRejected(a.id, true)}>Reject</Button>}
                      {confirmDel === a.id ? (
                        <>
                          <Button variant="danger" size="sm" disabled={busyId === a.id} onClick={() => remove(a.id)}>{busyId === a.id ? '…' : 'Confirm'}</Button>
                          <Button variant="ghost" size="sm" onClick={() => setConfirmDel(null)}>Cancel</Button>
                        </>
                      ) : (
                        <IconButton label={`Delete ${a.title}`} variant="ghost" onClick={() => setConfirmDel(a.id)}><Icon name="trash" size={15} /></IconButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CollapsibleSection>
    </div>
  );
}

/* ---- staff tab (system-wide admins) ---------------------------------- */

function StaffPanel() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['admin', 'staff'], queryFn: getStaff });
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editFirst, setEditFirst] = useState('');
  const [editLast, setEditLast] = useState('');

  const add = useMutation({
    mutationFn: () => addStaff(email.trim()),
    onSuccess: (r) => {
      setMsg({ kind: 'ok', text: r.invited
        ? `Invited ${email.trim()} as an admin. Once they accept the email and sign up, click “Sync client access” to give them every client.`
        : `${email.trim()} is now a system-wide admin with access to all clients.` });
      setEmail('');
      qc.invalidateQueries({ queryKey: ['admin', 'staff'] });
    },
    onError: (e) => setMsg({ kind: 'err', text: apiErrMsg(e, 'Could not add admin.') }),
  });

  const remove = useMutation({
    mutationFn: (m: StaffMember) => removeStaff(m.id),
    onSuccess: (_r, m) => {
      setMsg({ kind: 'ok', text: `Removed admin access for ${m.name}.` });
      qc.invalidateQueries({ queryKey: ['admin', 'staff'] });
    },
    onError: (e) => setMsg({ kind: 'err', text: apiErrMsg(e, 'Could not remove admin.') }),
  });

  const sync = useMutation({
    mutationFn: () => syncStaffAccess(),
    onSuccess: (r) =>
      setMsg({
        kind: r.failed ? 'err' : 'ok',
        text: `Access synced — ${r.admins} admin(s) across ${r.orgs} client(s), ${r.added} new membership(s) added`
          + (r.raised ? `, ${r.raised} member limit(s) raised` : '')
          + (r.failed ? `. ${r.failed} add(s) failed — likely a Clerk plan cap on org members.` : '.'),
      }),
    onError: (e) => setMsg({ kind: 'err', text: apiErrMsg(e, 'Sync failed.') }),
  });

  const startEdit = (m: StaffMember) => { setEditId(m.id); setEditFirst(m.firstName || ''); setEditLast(m.lastName || ''); };
  const editProfile = useMutation({
    mutationFn: () => updateStaffProfile(editId as string, { firstName: editFirst.trim(), lastName: editLast.trim() }),
    onSuccess: () => {
      setMsg({ kind: 'ok', text: 'Profile updated.' });
      setEditId(null);
      qc.invalidateQueries({ queryKey: ['admin', 'staff'] });
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] }); // manager/team names use these
    },
    onError: (e) => setMsg({ kind: 'err', text: apiErrMsg(e, 'Could not update profile.') }),
  });

  const reassign = useMutation({
    mutationFn: () => reassignAccounts(fromId, toId),
    onSuccess: (r) => {
      setMsg({ kind: 'ok', text: `Reassigned ${r.moved} role assignment(s) across all clients.` });
      setFromId(''); setToId('');
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] });
    },
    onError: (e) => setMsg({ kind: 'err', text: apiErrMsg(e, 'Reassign failed.') }),
  });

  const staff = data?.staff ?? [];

  return (
    <div className="dash-stack">
      <Card variant="dark" padding="lg" className="cq-hero">
        <div className="cq-hero__txt">
          <span className="cq-hero__eyebrow"><Icon name="shield-check" size={14} /> Triton admin</span>
          <h2>Staff &amp; access</h2>
          <p>System-wide admins can open this dashboard and every client&rsquo;s data. Enter an email: if they already have an account they&rsquo;re promoted instantly; if not, they get an admin invite and become an admin when they sign up. After a new admin signs up, click &ldquo;Sync client access&rdquo; to add them to every client.</p>
        </div>
        <Button variant="ghost" iconLeft={<Icon name="refresh-cw" size={16} />} onClick={() => sync.mutate()} disabled={sync.isPending}>
          {sync.isPending ? 'Syncing…' : 'Sync client access'}
        </Button>
      </Card>

      {msg && (
        <Card padding="lg">
          <p className="dash-muted" style={{ color: msg.kind === 'err' ? 'var(--danger)' : undefined }}>{msg.text}</p>
        </Card>
      )}

      <Card padding="lg">
        <form
          className="admin-prof__row"
          style={{ alignItems: 'flex-end' }}
          onSubmit={(e) => { e.preventDefault(); if (email.trim()) add.mutate(); }}
        >
          <Input
            label="Add a system-wide admin"
            type="email"
            placeholder="person@tritoncommerce.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button type="submit" variant="primary" disabled={!email.trim() || add.isPending}>
            {add.isPending ? 'Adding…' : 'Grant admin'}
          </Button>
        </form>
      </Card>

      <Card padding="lg">
        <h3 className="admin-sect__title">Reassign a leaving employee&rsquo;s accounts</h3>
        <p className="dash-muted" style={{ margin: '4px 0 12px' }}>Move all of one person&rsquo;s client assignments — content manager and every team role, across every client — to another admin.</p>
        <div className="admin-prof__row" style={{ alignItems: 'flex-end' }}>
          <label className="tr-field">
            <span className="tr-field__label">From (leaving)</span>
            <div className="tr-field__control">
              <select className="tr-select" value={fromId} onChange={(e) => setFromId(e.target.value)}>
                <option value="">Select…</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </label>
          <label className="tr-field">
            <span className="tr-field__label">To (takes over)</span>
            <div className="tr-field__control">
              <select className="tr-select" value={toId} onChange={(e) => setToId(e.target.value)}>
                <option value="">Select…</option>
                {staff.filter((s) => s.id !== fromId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </label>
          <Button variant="primary" disabled={!fromId || !toId || reassign.isPending}
            onClick={() => { if (confirm("Reassign all of this person's client roles to the selected admin?")) reassign.mutate(); }}>
            {reassign.isPending ? 'Reassigning…' : 'Reassign accounts'}
          </Button>
        </div>
      </Card>

      <Card padding={false} className="dash-leads">
        <div className="dash-panelhead dash-panelhead--pad">
          <div><h2>Current admins</h2><p>Everyone with portal-wide admin access.</p></div>
        </div>
        {isLoading ? (
          <div className="lead-none">Loading admins…</div>
        ) : isError ? (
          <div className="lead-none">Couldn’t load admins: {error instanceof Error ? error.message : 'error'}</div>
        ) : staff.length === 0 ? (
          <div className="lead-none">No admins yet.</div>
        ) : (
          <table className="dash-table">
            <thead>
              <tr><th>Name</th><th>Email</th><th></th></tr>
            </thead>
            <tbody>
              {staff.map((m) => {
                const editing = editId === m.id;
                const inputStyle: CSSProperties = { width: 120, padding: '4px 6px', border: '1px solid var(--border, #d1d5db)', borderRadius: 6, fontSize: 'var(--text-sm)' };
                return (
                  <tr key={m.id}>
                    <td>
                      {editing ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input autoFocus value={editFirst} placeholder="First" style={inputStyle} onChange={(e) => setEditFirst(e.target.value)} />
                          <input value={editLast} placeholder="Last" style={inputStyle} onChange={(e) => setEditLast(e.target.value)} />
                        </div>
                      ) : (
                        <div className="dash-cust">
                          <span>{m.name}</span>
                          {m.isSelf && <Badge variant="neutral">You</Badge>}
                        </div>
                      )}
                    </td>
                    <td className="dash-muted">{m.email || '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {editing ? (
                        <div className="admin-rowactions" style={{ justifyContent: 'flex-end' }}>
                          <Button variant="primary" size="sm" disabled={editProfile.isPending} onClick={() => editProfile.mutate()}>{editProfile.isPending ? 'Saving…' : 'Save'}</Button>
                          <Button variant="ghost" size="sm" onClick={() => setEditId(null)}>Cancel</Button>
                        </div>
                      ) : (
                        <div className="admin-rowactions" style={{ justifyContent: 'flex-end' }}>
                          <Button variant="ghost" size="sm" onClick={() => startEdit(m)}>Edit name</Button>
                          {!m.isSelf && (
                            <Button variant="danger" size="sm" disabled={remove.isPending}
                              onClick={() => { if (confirm(`Remove admin access for ${m.name}?`)) remove.mutate(m); }}>
                              Remove
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

/* ---- integrations tab ------------------------------------------------- */

function IntegrationsPanel() {
  const { data, isLoading, isError } = useQuery({ queryKey: ['admin', 'roam'], queryFn: getRoamConfig });
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!data?.webhookUrl) return;
    navigator.clipboard?.writeText(data.webhookUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  return (
    <section className="admin-sect">
      <div><h2>RoamHQ transcripts</h2><p>Auto-attach interview transcripts to scheduled interviews. Scheduling an interview creates a Roam voice meeting link; when Roam finishes the transcript it posts to the webhook below and it&apos;s attached automatically (Google Meet calls work too, matched by attendee + time).</p></div>
      {isLoading ? <p>Loading…</p> : isError || !data ? <p className="err">Could not load Roam settings.</p> : (
        <div className="stack-sm" style={{ display: 'grid', gap: 12, maxWidth: 720 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Badge variant={data.tokenSet ? 'success' : 'warning'}>Access token {data.tokenSet ? 'set' : 'missing'}</Badge>
            <Badge variant={data.signingSecretSet ? 'success' : 'warning'}>Signing secret {data.signingSecretSet ? 'set' : 'missing'}</Badge>
            <Badge variant={data.dialIn ? 'success' : 'neutral'}>{data.dialIn ? `Dial-in: ${data.dialIn}` : 'No dial-in set'}</Badge>
          </div>
          <label>
            <div className="lbl">Webhook URL — add in Roam Admin → Developer → Webhooks, event <code>transcript:saved</code></div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Input readOnly value={data.webhookUrl} onFocus={(e) => e.currentTarget.select()} />
              <Button type="button" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
            </div>
          </label>
          {!data.tokenSet && <p className="muted">Set <code>ROAM_ACCESS_TOKEN</code> (and <code>ROAM_SIGNING_SECRET</code>) as Worker secrets to enable this.</p>}
        </div>
      )}
      <BackupsCard />
      <EmailLogCard />
      <AiModelsCard />
      <DictationCard />
      <InterviewContextCard />
      <ContentAutomationCard />
      <SignalProbeCard />
    </section>
  );
}

/* Every email the Worker tried to send, newest first, and a button that sends
   one to you. The AI-interview notification failed silently for weeks because a
   rejected send only reached the Worker console; this is where it shows now. */
/* Which Claude model runs each kind of work, with a per-tier override that
   takes effect within a minute and no deploy, and 30 days of usage beside it
   so a switch can be judged instead of guessed. */
function AiModelsCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'ai'], queryFn: getAiModels });
  const rows = data?.tiers ?? [];
  const total = rows.reduce((n, t) => n + t.usage.reduce((m, u) => m + u.costUsd, 0), 0);
  return (
    <div style={{ marginTop: 24 }}>
      <h2>AI models</h2>
      <p>Which Claude model runs each kind of work. Defaults live in the code; an override here takes effect within a minute, no deploy. Usage is the last 30 days, cost estimated from list prices.{rows.length ? ` About $${total.toFixed(2)} across all tiers.` : ''}</p>
      {isLoading ? <p className="dash-muted">Loading…</p> : (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table className="dash-table">
            <thead><tr><th>Tier</th><th>Model</th><th>Effort</th><th>30d calls</th><th>Tokens in / out</th><th>Est. cost</th><th></th></tr></thead>
            <tbody>{rows.map((t) => <AiTierRowEditor key={t.tier} row={t} onChanged={() => qc.invalidateQueries({ queryKey: ['admin', 'ai'] })} />)}</tbody>
          </table>
        </div>
      )}
      {data && data.recentErrors.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary className="dash-muted">Recent failures ({data.recentErrors.length})</summary>
          <ul className="dash-muted" style={{ fontSize: 'var(--text-xs)', margin: '6px 0 0', paddingLeft: 18 }}>
            {data.recentErrors.map((e, i) => <li key={i}>{e.at.slice(0, 16)} · {e.tier} / {e.purpose.replace(/_/g, ' ')} · {e.model} — {e.error.slice(0, 160)}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}

/* A rollout switch: one value for everyone, overridable per client, no
   deploy either way (worker/rollout.ts). A feature goes to one pilot account
   first, then to everyone, and an account that prefers the old way is opted
   back out here. */
function RolloutCard({ name, title, labels, children, extra }: {
  name: string; title: string; labels: Record<string, string>; children: ReactNode; extra?: ReactNode;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'rollout', name], queryFn: () => getRollout(name) });
  const { data: clients } = useQuery({ queryKey: ADMIN_CLIENTS_KEY, queryFn: getAdminClients });
  const [busy, setBusy] = useState(false);
  const [orgId, setOrgId] = useState('');
  const [value, setValue] = useState('');
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); await qc.invalidateQueries({ queryKey: ['admin', 'rollout', name] }); } finally { setBusy(false); }
  };
  const overridden = new Set((data?.overrides ?? []).map((o) => o.orgId));
  const pickable = (clients?.clients ?? []).filter((c) => !overridden.has(c.orgId));
  const label = (v: string) => labels[v] ?? v;
  const pilot = value || data?.options[data.options.length - 1] || '';
  return (
    <div style={{ marginTop: 24 }}>
      <h2>{title}</h2>
      <p>{children}</p>
      {isLoading || !data ? <p className="dash-muted">Loading…</p> : (
        <>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
            <label>Everyone:{' '}
              <select className="tr-select" value={data.value} disabled={busy} onChange={(e) => void run(() => setRolloutValue(name, e.target.value))}>
                {data.options.map((o) => <option key={o} value={o}>{label(o)}</option>)}
              </select>
            </label>
            {extra}
          </div>
          {data.overrides.length > 0 && (
            <table className="dash-table" style={{ marginTop: 10 }}>
              <thead><tr><th>Client</th><th>Setting</th><th></th></tr></thead>
              <tbody>{data.overrides.map((o) => (
                <tr key={o.orgId}>
                  <td>{o.clientName || o.orgId}</td>
                  <td>{label(o.value)}</td>
                  <td><Button size="sm" variant="ghost" disabled={busy} onClick={() => void run(() => clearRolloutOverride(name, o.orgId))}>Remove</Button></td>
                </tr>
              ))}</tbody>
            </table>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
            <select className="tr-select" value={orgId} disabled={busy} onChange={(e) => setOrgId(e.target.value)} aria-label="Client">
              <option value="">Client…</option>
              {pickable.map((c) => <option key={c.orgId} value={c.orgId}>{c.name}</option>)}
            </select>
            <select className="tr-select" value={pilot} disabled={busy} onChange={(e) => setValue(e.target.value)} aria-label="Setting">
              {data.options.map((o) => <option key={o} value={o}>{label(o)}</option>)}
            </select>
            <Button size="sm" variant="outline" disabled={busy || !orgId} onClick={() => void run(async () => { await setRolloutValue(name, pilot, orgId); setOrgId(''); })}>Set for this client</Button>
          </div>
        </>
      )}
    </div>
  );
}

/* Which engine turns a spoken answer into text. */
function DictationCard() {
  const { data } = useQuery({ queryKey: ['admin', 'dictation'], queryFn: getDictationAdmin });
  const u = data?.usage30d;
  return (
    <RolloutCard name="dictation_engine" title="Dictation" labels={{ browser: 'Browser', whisper: 'Whisper' }}
      extra={<>
        {data && !data.available && <span className="dash-muted" style={{ fontSize: 'var(--text-sm)' }}>The Workers AI binding is not on this deployment, so every client stays on the browser engine whatever is set here.</span>}
        {u && <span className="dash-muted" style={{ fontSize: 'var(--text-sm)' }}>Last 30 days: {u.calls} phrases · {u.minutes} min · ${u.costUsd.toFixed(2)}{u.failures ? ` · ${u.failures} failed` : ''}{u.avgMs ? ` · ${(u.avgMs / 1000).toFixed(1)}s each` : ''}</span>}
      </>}>
      How a spoken answer becomes text in the AI interview, the gap questions and Expert Studio. <strong>Browser</strong> is the device’s own speech recognition: free and word-by-word, but some phones echo phrases. <strong>Whisper</strong> records and transcribes on Workers AI: the same on every device, a phrase at a time, with filler words tidied out and nothing added. Audio is never stored.
    </RolloutCard>
  );
}

/* What the AI interviewer knows before its first question. */
function InterviewContextCard() {
  return (
    <RolloutCard name="interview_context" title="Interview context" labels={{ off: 'Off', on: 'On' }}>
      <strong>On</strong>, the AI interviewer is told who it is talking to (the client profile), what this session is about (the project), what this expert has already said in earlier interviews (so it does not ask again), and where to start — an entry point that changes from session to session. The five areas and the finish are unchanged. <strong>Off</strong> is the interview exactly as before. Pilot it on one account first; a prompt that suits one trade may not suit another.
    </RolloutCard>
  );
}

/* The two auto-write switches. Global, like the model tiers beside them:
   Triton turns the feature on for everyone, not account by account. Each
   line says what happens and who hears about it, because the switch
   replaces an email people are used to getting. */
function ContentAutomationCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'blogSettings'], queryFn: getBlogSettings });
  const [busy, setBusy] = useState(false);
  const flip = async (patch: Partial<BlogSettings>) => {
    setBusy(true);
    try { await setBlogSettings(patch); await qc.invalidateQueries({ queryKey: ['admin', 'blogSettings'] }); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 24 }}>
      <h2>Content automation</h2>
      <p>Write the article the moment there is something to write it from, save it as a draft, and email the contentIQ Editor (else the Content Manager). Nothing is pushed to a site — that stays a button. Every auto-written blog is audited.</p>
      {isLoading || !data ? <p className="dash-muted">Loading…</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type="checkbox" checked={data.autoWriteTopics} disabled={busy} onChange={(e) => void flip({ autoWriteTopics: e.target.checked })} style={{ marginTop: 3 }} />
            <span><strong>Approved blog topics</strong><br /><span className="dash-muted" style={{ fontSize: 'var(--text-sm)' }}>When a topic is approved it is written within five minutes. Replaces the “write it” email with “it’s written”.</span></span>
          </label>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type="checkbox" checked={data.autoWriteInterviews} disabled={busy} onChange={(e) => void flip({ autoWriteInterviews: e.target.checked })} style={{ marginTop: 3 }} />
            <span><strong>Finished AI interviews</strong><br /><span className="dash-muted" style={{ fontSize: 'var(--text-sm)' }}>A knowledge session becomes a blog (evidence writer), a project interview a case study, a few minutes after the client finishes. Replaces the interview-complete email.</span></span>
          </label>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type="checkbox" checked={data.waitingDigestDaily} disabled={busy} onChange={(e) => void flip({ waitingDigestDaily: e.target.checked })} style={{ marginTop: 3 }} />
            <span><strong>Daily “waiting on you” email</strong><br /><span className="dash-muted" style={{ fontSize: 'var(--text-sm)' }}>Every morning at 8 ET instead of Mondays only, to each person with something in their queue. Never sent when there is nothing in it.</span></span>
          </label>
        </div>
      )}
    </div>
  );
}

function AiTierRowEditor({ row, onChanged }: { row: AiTierRow; onChanged: () => void }) {
  const [model, setModel] = useState(row.effective.model);
  const [effort, setEffort] = useState(row.effective.effort ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const dirty = model.trim() !== row.effective.model || (effort || '') !== (row.effective.effort ?? '');
  const calls = row.usage.reduce((n, u) => n + u.calls, 0);
  const failures = row.usage.reduce((n, u) => n + u.failures, 0);
  const tokIn = row.usage.reduce((n, u) => n + u.inputTokens, 0);
  const tokOut = row.usage.reduce((n, u) => n + u.outputTokens, 0);
  const cost = row.usage.reduce((n, u) => n + u.costUsd, 0);
  const fmtK = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}k` : String(n));
  const run = async (fn: () => Promise<string>) => {
    setBusy(true); setMsg(null);
    try { setMsg(await fn()); onChanged(); }
    catch (e) { setMsg(apiErrMsg(e, 'That failed.')); }
    finally { setBusy(false); }
  };
  return (
    <tr>
      <td><strong>{row.label}</strong>{row.override && <> <Badge variant="warning">override</Badge></>}<div className="dash-muted" style={{ fontSize: 'var(--text-xs)', maxWidth: 320 }}>{row.purpose}</div></td>
      <td><input className="tr-input" style={{ minWidth: 190, fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }} value={model} onChange={(e) => setModel(e.target.value)} spellCheck={false} /></td>
      <td>
        <select className="tr-select" value={/haiku/.test(model) ? '' : effort} disabled={/haiku/.test(model)} title={/haiku/.test(model) ? 'Haiku has no effort control' : undefined} onChange={(e) => setEffort(e.target.value)}>
          <option value="">— none —</option>
          {AI_EFFORTS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </td>
      <td>{calls}{failures ? <span className="admin-modal__err"> ({failures} failed)</span> : null}</td>
      <td className="dash-muted" style={{ whiteSpace: 'nowrap' }}>{fmtK(tokIn)} / {fmtK(tokOut)}</td>
      <td>${cost.toFixed(2)}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <Button size="sm" variant="outline" disabled={busy || !dirty} onClick={() => run(async () => { const r = await setAiModel(row.tier, { model: model.trim(), ...(effort ? { effort: effort as AiTierRow['effective']['effort'] } : {}) }); return `Saved: ${r.effective.model}${r.effective.effort ? ` · ${r.effective.effort}` : ''}.`; })}>Save</Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(async () => { const r = await testAiModel(row.tier); if (!r.ok) throw new Error(r.error || 'failed'); return `OK — ${r.model} answered in ${r.ms} ms.`; })}>Test</Button>
          {row.override && <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(async () => { const r = await resetAiModel(row.tier); setModel(r.effective.model); setEffort(r.effective.effort ?? ''); return `Back to the default: ${r.effective.model}.`; })}>Reset</Button>}
        </div>
        {msg && <div className="dash-muted" style={{ fontSize: 'var(--text-xs)', marginTop: 4, maxWidth: 260 }}>{msg}</div>}
      </td>
    </tr>
  );
}

function EmailLogCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'emails'], queryFn: getEmailLog });
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState<string | null>(null);
  const test = async () => {
    setTesting(true); setTestMsg(null);
    try {
      const r = await sendTestEmail();
      setTestMsg(`Sent to ${r.to.join(', ')} — check your inbox.`);
    } catch (e) {
      setTestMsg(apiErrMsg(e, 'The test email failed.'));
    } finally {
      setTesting(false);
      qc.invalidateQueries({ queryKey: ['admin', 'emails'] });
    }
  };
  const rows = data?.emails ?? [];
  const failed = rows.filter((r) => !r.ok).length;
  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2>Email delivery</h2>
          <p>Every message tritonIQ tried to send, and whether SendGrid accepted it.{failed ? ` ${failed} of the last ${rows.length} failed.` : rows.length ? ` All of the last ${rows.length} were accepted.` : ''}</p>
        </div>
        <Button variant="outline" size="sm" disabled={testing} onClick={test} iconLeft={<Icon name="mail" size={14} />}>
          {testing ? 'Sending…' : 'Send me a test email'}
        </Button>
      </div>
      {testMsg && <p className="dash-muted" style={{ marginTop: 6 }}>{testMsg}</p>}
      {isLoading ? <p className="dash-muted">Loading…</p> : rows.length === 0 ? (
        <p className="dash-muted">No sends recorded yet. Attempts are logged from the moment this shipped.</p>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table className="dash-table">
            <thead><tr><th>When (UTC)</th><th>What</th><th>Client</th><th>To</th><th>Result</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="dash-muted" style={{ whiteSpace: 'nowrap' }}>{r.createdAt.slice(0, 16)}</td>
                  <td>{r.kind.replace(/_/g, ' ')}</td>
                  <td>{r.clientName ?? <span className="dash-muted">—</span>}</td>
                  <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.recipients}>{r.recipients || <span className="dash-muted">nobody</span>}</td>
                  <td>{r.ok ? <Badge variant="success" dot>Accepted</Badge> : <span className="admin-modal__err" title={r.error ?? ''}>{(r.error ?? 'failed').slice(0, 90)}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Signal Return E0-1: one-click check that the Google OAuth token was re-consented
// with both scopes and that the Ads + Data Manager APIs answer. Read-only.
function SignalProbeCard() {
  const [probe, setProbe] = useState<SignalProbe | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const run = async () => {
    setBusy(true); setErr(''); setProbe(null);
    try { setProbe(await getSignalProbe()); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const row = (label: string, c: { ok: boolean; detail: string }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, fontSize: 'var(--text-sm)' }}>
      <Badge variant={c.ok ? 'success' : 'warning'} dot>{c.ok ? 'PASS' : 'FAIL'}</Badge>
      <strong style={{ whiteSpace: 'nowrap' }}>{label}</strong>
      <span className="dash-muted" style={{ wordBreak: 'break-word' }}>{c.detail}</span>
    </div>
  );
  return (
    <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid var(--border-subtle)' }}>
      <div><h2>Signal Return — Google connection</h2><p>Verifies the Google Ads OAuth token carries both scopes (<code>adwords</code> + <code>datamanager</code>) and that the Ads API and Data Manager API respond. Read-only — sends nothing to any client account.</p></div>
      <div style={{ margin: '10px 0' }}>
        <Button size="sm" variant="primary" disabled={busy} onClick={run}>{busy ? 'Testing…' : 'Run connection test'}</Button>
      </div>
      {err && <p className="err">{err}</p>}
      {probe && (
        <div style={{ display: 'grid', gap: 6, maxWidth: 720 }}>
          {row('Token exchange', probe.exchange)}
          {row('Scope: adwords', probe.scopes.adwords)}
          {row('Scope: datamanager', probe.scopes.datamanager)}
          {row('Google Ads API', probe.adsApi)}
          {row('Data Manager API', probe.dataManager)}
        </div>
      )}
    </div>
  );
}

function fmtBytes(n: number): string {
  return n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`;
}

function BackupsCard() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin', 'backups'], queryFn: getBackups });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const run = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await backupNow();
      if (r.error) setMsg(r.error);
      else { setMsg(`Backed up ${r.key} (${fmtBytes(r.bytes || 0)}).`); qc.invalidateQueries({ queryKey: ['admin', 'backups'] }); }
    } catch (e) { setMsg(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const backups = data?.backups ?? [];
  return (
    <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid var(--border-subtle)' }}>
      <div><h2>Database backups</h2><p>Automatic nightly D1 backup (~3am ET) to a private R2 bucket, kept 7 days. Restore a dump with <code>wrangler d1 execute tritoniq-db --file=&lt;file&gt;.sql</code>.</p></div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '10px 0' }}>
        <Badge variant={data?.configured ? 'success' : 'warning'}>{data?.configured ? 'Bucket connected' : 'Bucket not configured'}</Badge>
        <Button size="sm" variant="primary" disabled={busy || !data?.configured} onClick={run}>{busy ? 'Backing up…' : 'Back up now'}</Button>
        {msg && <span className="dash-muted">{msg}</span>}
      </div>
      {backups.length > 0 && (
        <div className="stack-sm" style={{ display: 'grid', gap: 6, maxWidth: 560 }}>
          {backups.map((b) => (
            <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 'var(--text-sm)' }}>
              <Icon name="shield-check" size={14} />
              <span style={{ flex: 1, fontFamily: 'var(--font-mono)' }}>{b.key}</span>
              <span className="dash-muted">{fmtBytes(b.size)}</span>
              <button className="admin-linkbtn" onClick={() => downloadBackup(b.key)}>Download</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ADMIN_TABS = ['clients', 'content', 'staff', 'integrations', 'referrals', 'reviews-widget'];
export function Admin() {
  /* ?tab=content deep-links from emails (topic batches to approve). */
  const [tab, setTab] = useState(() => { const t = new URLSearchParams(window.location.search).get('tab') || ''; return ADMIN_TABS.includes(t) ? t : 'clients'; });
  return (
    <div className="dash-stack">
      <div className="lead-tabsbar">
        <Tabs
          variant="pill"
          value={tab}
          onChange={setTab}
          items={[
            { value: 'clients', label: 'Clients & connections' },
            { value: 'content', label: 'Content' },
            { value: 'staff', label: 'Staff & access' },
            { value: 'integrations', label: 'Integrations' },
            { value: 'referrals', label: 'Referral Rewards' },
            { value: 'reviews-widget', label: 'Reviews widget' },
          ]}
        />
      </div>
      {tab === 'clients' ? <ClientsPanel /> : tab === 'content' ? <ContentPanel /> : tab === 'staff' ? <StaffPanel /> : tab === 'referrals' ? <ReferralsBoard /> : tab === 'reviews-widget' ? <ReviewsWidgetBoard /> : <IntegrationsPanel />}
    </div>
  );
}
